import { gql } from '@apollo/client';

/**
 * Spec 0018 (career center), increment A — ERP-scope mutations.
 *
 * Operation names differ from the backend field names
 * (`enrollCareerMembers`, `updateCareerMember`, `upsertCareerLesson`) to avoid
 * the codegen result-type collapse. All three return the project envelope
 * (`success` + `customErrors`).
 */

/**
 * Bulk enrolment. `studentIds` and `groupId` are additive — the backend unions
 * them — and re-running is safe: an already-enrolled student lands in
 * `skipped`, an archived one in `reactivated`. The three counters drive the
 * result toast.
 */
export const EnrollCareerMembersMutation = gql`
  mutation EnrollToCareerCenter(
    $studentIds: [ID!]
    $groupId: ID
    $directionId: ID
  ) {
    enrollCareerMembers(
      studentIds: $studentIds
      groupId: $groupId
      directionId: $directionId
    ) {
      success
      customErrors {
        field
        message
        code
      }
      created
      reactivated
      skipped
    }
  }
`;

export const UpdateCareerMemberMutation = gql`
  mutation SaveCareerMember($id: ID!, $input: CareerMemberInput!) {
    updateCareerMember(id: $id, input: $input) {
      success
      customErrors {
        field
        message
        code
      }
      member {
        id
        studentId
        studentName
        studentEmail
        groupId
        groupName
        directionId
        directionTitle
        status
        enrolledAt
        notes
        lessonsCompleted
        lessonsTotal
      }
    }
  }
`;

/**
 * Create (no `id`) or update (`id`) a lesson of the single career track. The
 * returned `lesson` selection mirrors `ErpCareerLessons` field-for-field
 * (spec 0020 added `videoUrl` + `attachments`, then `stats` + `quizQuestions`)
 * so `saveLesson` can hand the saved row — freshly minted with an `id` —
 * straight back to the form, which then unlocks the video/attachment/quiz
 * controls. The selection must stay in lock-step with the query: `saveLesson`
 * is typed to return the full `ICareerLesson`.
 */
export const UpsertCareerLessonMutation = gql`
  mutation SaveCareerLesson($id: ID, $input: CareerLessonInput!) {
    upsertCareerLesson(id: $id, input: $input) {
      success
      customErrors {
        field
        message
        code
      }
      lesson {
        id
        order
        title
        summary
        content
        durationMin
        completionKind
        artifactHint
        isPublished
        videoUrl
        attachments {
          id
          originalName
          contentType
          sizeBytes
          url
        }
        stats {
          openedCount
          completedCount
          membersTotal
        }
        quizQuestions {
          id
          order
          text
          options
          correctIndexes
        }
      }
    }
  }
`;

/* ── Spec 0020 — lesson authoring parity: video + attachments ─────────── */

/**
 * Attach (or replace) the lesson's video in a single multipart request: the
 * picked `File` travels as an `Upload` scalar over the GraphQL multipart request
 * spec (same terminating `createUploadLink` as `uploadStudentDiploma`), and the
 * backend writes the object server-side — no presigned PUT, no bucket CORS. The
 * returned `lesson` echoes the fresh media so the form swaps its local state
 * wholesale.
 *
 * Operation names throughout this block differ from the backend field names
 * (`uploadCareerLessonVideo`, `clearCareerLessonVideo`,
 * `addCareerLessonAttachment`, `removeCareerLessonAttachment`) to avoid the
 * codegen result-type collapse.
 */
export const UploadCareerLessonVideoMutation = gql`
  mutation AttachCareerLessonVideo($lessonId: ID!, $video: Upload!) {
    uploadCareerLessonVideo(lessonId: $lessonId, video: $video) {
      success
      customErrors {
        field
        message
        code
      }
      lesson {
        id
        videoUrl
        attachments {
          id
          originalName
          contentType
          sizeBytes
          url
        }
      }
    }
  }
`;

/** Detach the lesson's video. */
export const ClearCareerLessonVideoMutation = gql`
  mutation RemoveCareerLessonVideo($lessonId: ID!) {
    clearCareerLessonVideo(lessonId: $lessonId) {
      success
      customErrors {
        field
        message
        code
      }
      lesson {
        id
        videoUrl
        attachments {
          id
          originalName
          contentType
          sizeBytes
          url
        }
      }
    }
  }
`;

/**
 * Attach one file. `file` travels as an `Upload` scalar over the GraphQL
 * multipart request spec (same terminating `createUploadLink` as
 * `uploadStudentDiploma`). The returned `lesson.attachments` is the full,
 * updated list — the form replaces its local list with it wholesale.
 */
export const AddCareerLessonAttachmentMutation = gql`
  mutation AttachCareerLessonFile($lessonId: ID!, $file: Upload!) {
    addCareerLessonAttachment(lessonId: $lessonId, file: $file) {
      success
      customErrors {
        field
        message
        code
      }
      attachment {
        id
        originalName
        contentType
        sizeBytes
        url
      }
      lesson {
        id
        videoUrl
        attachments {
          id
          originalName
          contentType
          sizeBytes
          url
        }
      }
    }
  }
`;

/** Remove one attachment by id (returns only the `ok` flag — no lesson). */
export const RemoveCareerLessonAttachmentMutation = gql`
  mutation DetachCareerLessonFile($attachmentId: ID!) {
    removeCareerLessonAttachment(attachmentId: $attachmentId) {
      success
      ok
      customErrors {
        field
        message
        code
      }
    }
  }
`;

/* ── Spec 0020 Фаза C — lesson quiz editor (ERP scope, CAREER_ROLES) ──── */

/**
 * Replace the whole quiz for one lesson. `questions` is the full desired set —
 * the backend swaps the lesson's questions for it wholesale, and `order` follows
 * array position. Each question needs ≥2 options and ≥1 correct index; a
 * violation comes back as a `customErrors` field entry (surfaced by the form),
 * not a throw.
 *
 * The wire input field is `correctIndexes` (Graphene camel-cases the Python
 * `correct_indexes`); the returned `quizQuestions` selection matches
 * `ErpCareerLessons` field-for-field. Op name differs from the
 * `setCareerLessonQuiz` field to avoid the codegen result-type collapse.
 */
export const SetCareerLessonQuizMutation = gql`
  mutation SaveCareerLessonQuiz(
    $lessonId: ID!
    $questions: [CareerQuizQuestionInput!]!
  ) {
    setCareerLessonQuiz(lessonId: $lessonId, questions: $questions) {
      success
      customErrors {
        field
        message
        code
      }
      lesson {
        id
        quizQuestions {
          id
          order
          text
          options
          correctIndexes
        }
      }
    }
  }
`;

/* ── Increment B — vacancies + placement ─────────────────────────────── */

const VACANCY_FIELDS = gql`
  fragment ErpVacancyFields on VacancyType {
    id
    title
    companyName
    applyUrl
    location
    workMode
    seniority
    employmentType
    salaryMin
    salaryMax
    salaryCurrency
    description
    status
    publishedAt
    expiresAt
    createdAt
    directionIds
    directionTitles
    skillIds
    skillNames
    appliedCount
    source
    createdByName
    reviewedByName
    reviewedAt
    updatedAt
    duplicateOf {
      vacancyId
      title
      companyName
      status
      score
    }
  }
`;

/**
 * Create (no `id`) or update (`id`) a vacancy. A new vacancy is born DRAFT;
 * status is moved separately through `SetVacancyStatus`. Operation name differs
 * from the `upsertVacancy` field to keep a dedicated result type.
 */
export const UpsertVacancyMutation = gql`
  mutation UpsertVacancy($id: ID, $input: VacancyInput!) {
    upsertVacancy(id: $id, input: $input) {
      success
      customErrors {
        field
        message
        code
      }
      vacancy {
        ...ErpVacancyFields
      }
    }
  }
  ${VACANCY_FIELDS}
`;

/**
 * Move one vacancy along the DRAFT→APPROVED→PUBLISHED→REJECTED→ARCHIVED
 * workflow. `status` is a plain String on the wire.
 */
/**
 * Hard-delete a vacancy. The backend allows it only for DRAFT/REJECTED rows
 * with no placement attached — everything else refuses with `vacancy_locked`
 * and points at the archive, because `VacancyInteraction` cascades and would
 * take students' saved marks and the funnel's applied history with it.
 * `deletedInteractions` is what the confirm dialog shows as the cost.
 */
export const DeleteVacancyMutation = gql`
  mutation DeleteVacancy($id: ID!) {
    deleteVacancy(id: $id) {
      success
      ok
      deletedInteractions
      customErrors {
        field
        message
        code
      }
    }
  }
`;

export const SetVacancyStatusMutation = gql`
  mutation SetVacancyStatus($id: ID!, $status: String!) {
    setVacancyStatus(id: $id, status: $status) {
      success
      customErrors {
        field
        message
        code
      }
      vacancy {
        ...ErpVacancyFields
      }
    }
  }
  ${VACANCY_FIELDS}
`;

/** Publish many drafts at once; `published` is the count actually flipped. */
export const BulkPublishVacanciesMutation = gql`
  mutation BulkPublishVacancies($ids: [ID!]!) {
    bulkPublishVacancies(ids: $ids) {
      success
      customErrors {
        field
        message
        code
      }
      published
    }
  }
`;

/** Record a graduate placement (the centre's KPI). */
export const RecordPlacementMutation = gql`
  mutation RecordPlacement($memberId: ID!, $input: PlacementInput!) {
    recordPlacement(memberId: $memberId, input: $input) {
      success
      customErrors {
        field
        message
        code
      }
      placement {
        id
        memberId
        studentName
        companyName
        position
        startedAt
        confirmedAt
      }
    }
  }
`;

/**
 * Move many vacancies at once (spec 0035) — the queue-draining action.
 *
 * `BulkPublishVacancies` only ever published, so rejecting a night's agent
 * drafts meant one mutation per row, each triggering a full table reload.
 * This covers every status and is set-based on the server.
 */
export const BulkSetVacancyStatusMutation = gql`
  mutation BulkSetVacancyStatus($ids: [ID!]!, $status: String!) {
    bulkSetVacancyStatus(ids: $ids, status: $status) {
      success
      moved
      customErrors {
        field
        message
        code
      }
    }
  }
`;
